import { useEffect, useState } from 'react';
import { securityConfig, isSecurityEnabledForPath, fetchAndApplySecurityConfig } from './config';

// Security event handlers
const preventCopy = (e: ClipboardEvent) => {
  e.preventDefault();
  e.stopPropagation();
  return false;
};

const preventSelection = (e: Event) => {
  // Allow selection on interactive elements
  const target = e.target as HTMLElement;

  // First check direct element types
  if (target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'BUTTON' ||
    target.tagName === 'A') {
    return true;
  }

  // Then check for role-based elements using Element.closest
  if (target instanceof Element) {
    if (target.closest('[role="button"]') ||
      target.closest('[role="tab"]') ||
      target.closest('[role="menuitem"]')) {
      return true;
    }
  }

  e.preventDefault();
  e.stopPropagation();
  return false;
};

const preventContextMenu = (e: MouseEvent) => {
  // Allow context menu on input elements
  const target = e.target as HTMLElement;
  if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
    return true;
  }

  e.preventDefault();
  e.stopPropagation();
  return false;
};

const preventKeyboardShortcuts = (e: KeyboardEvent) => {
  // Prevent copy shortcuts
  if (securityConfig.features.preventCopy &&
    ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'C'))
  ) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }

  // Prevent DevTools shortcuts
  if (securityConfig.features.preventDevTools && (
    (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
    (e.metaKey && e.altKey && e.key === 'I') ||
    e.key === 'F12'
  )) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
};

// Apply CSS to prevent selection while allowing UI interactions
const applyNoSelectCSS = () => {
  const style = document.createElement('style');
  style.textContent = `
    body > *:not(input):not(textarea):not(button):not(a):not([role="button"]):not([role="tab"]):not([role="menuitem"]) {
      -webkit-user-select: none !important;
      -moz-user-select: none !important;
      -ms-user-select: none !important;
      user-select: none !important;
    }
    
    input, textarea, button, a, [role="button"], [role="tab"], [role="menuitem"] {
      -webkit-user-select: text !important;
      -moz-user-select: text !important;
      -ms-user-select: text !important;
      user-select: text !important;
    }
  `;
  document.head.appendChild(style);
  return style;
};

// Method 4: Element inspection check
const preventInspection = (e: MouseEvent) => {
  const target = e.target as HTMLElement;

  // First check direct element types
  if (target.tagName === 'BUTTON' ||
    target.tagName === 'A') {
    return true;
  }

  // Then check for role-based elements using Element.closest
  if (target instanceof Element) {
    if (target.closest('[role="button"]') ||
      target.closest('[role="tab"]') ||
      target.closest('[role="menuitem"]')) {
      return true;
    }
  }

  // Only prevent if modifier keys are used (likely inspection attempt)
  if (e.ctrlKey || e.shiftKey || e.altKey || e.metaKey) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
};

// Enhanced DevTools detection
const createDevToolsDetector = () => {
  let devToolsCounter = 0;
  const threshold = 100;

  // Method 1: Size check
  const checkWindowSize = () => {
    const widthThreshold = window.outerWidth - window.innerWidth > 160;
    const heightThreshold = window.outerHeight - window.innerHeight > 160;
    return widthThreshold || heightThreshold;
  };

  // Method 2: Debugger check
  const checkDebugger = () => {
    const startTime = new Date().getTime();
    debugger;
    return new Date().getTime() - startTime > 100;
  };

  // Method 3: Console timing check
  const checkConsole = () => {
    const time = performance.now();
    console.log('Checking security...');
    console.clear();
    return performance.now() - time > 20;
  };

  // Method 5: Dev tools feature detection
  const checkDevToolsFeatures = () => {
    const features = [
      'debug',
      'profile',
      'profileEnd',
      'clear',
      'dir',
      'dirxml',
      'trace',
      'group',
      'groupCollapsed',
      'groupEnd'
    ];
    return features.some(feature => {
      try {
        // @ts-ignore
        return window.console[feature].toString().includes('[native code]');
      } catch {
        return false;
      }
    });
  };

  return {
    check: () => {
      try {
        if (
          checkWindowSize() ||
          checkDebugger() ||
          checkConsole() ||
          checkDevToolsFeatures()
        ) {
          devToolsCounter++;
          if (devToolsCounter >= threshold) {
            return true;
          }
        } else {
          devToolsCounter = Math.max(0, devToolsCounter - 1);
        }
      } catch (e) {
        // Silent fail to avoid breaking anything
      }
      return false;
    },
    preventInspection,
    reset: () => {
      devToolsCounter = 0;
    }
  };
};

// Custom hook to apply security measures
export const useSecurityMeasures = (options?: {
  preventDevTools?: boolean;
  preventCopy?: boolean;
  preventRightClick?: boolean;
  preventKeyboardShortcuts?: boolean;
  disableConsole?: boolean;
  currentPath?: string;
}) => {
  const [configLoaded, setConfigLoaded] = useState(false);
  const currentPath = options?.currentPath ?? (typeof window !== 'undefined' ? window.location.pathname : '/');

  // Hydrate config from gRPC on boot
  useEffect(() => {
    fetchAndApplySecurityConfig().finally(() => {
      setConfigLoaded(true);
    });
  }, []);

  useEffect(() => {
    // Wait for the backend response before applying rules
    if (!configLoaded) return;
    // If security is not enabled globally, return early
    if (!securityConfig.enabled) {
      return;
    }

    // If we're in a Router context and the path is whitelisted, return early
    if (currentPath !== '/' && !isSecurityEnabledForPath(currentPath)) {
      return;
    }

    const cleanupFunctions: (() => void)[] = [];

    // Copy and selection prevention
    if (securityConfig.features.preventCopy) {
      // Apply CSS first
      const noSelectStyle = applyNoSelectCSS();
      cleanupFunctions.push(() => noSelectStyle.remove());

      // Add event listeners
      const events = [
        { name: 'copy', handler: preventCopy },
        { name: 'cut', handler: preventCopy },
        { name: 'paste', handler: preventCopy },
        { name: 'selectstart', handler: preventSelection },
        // Remove mousedown and mouseup to allow UI interactions
        { name: 'keydown', handler: preventKeyboardShortcuts },
      ];

      events.forEach(({ name, handler }) => {
        document.addEventListener(name, handler as EventListener, { capture: true });
        cleanupFunctions.push(() =>
          document.removeEventListener(name, handler as EventListener, { capture: true })
        );
      });
    }

    // Right-click prevention
    if (securityConfig.features.preventRightClick) {
      document.addEventListener('contextmenu', preventContextMenu, { capture: true });
      cleanupFunctions.push(() =>
        document.removeEventListener('contextmenu', preventContextMenu, { capture: true })
      );
    }

    // DevTools detection
    if (securityConfig.features.preventDevTools) {
      const detector = createDevToolsDetector();

      // Add inspection prevention
      document.addEventListener('mousedown', detector.preventInspection, true);
      document.addEventListener('mouseover', detector.preventInspection, true);
      document.addEventListener('contextmenu', detector.preventInspection, true);

      // Add keyboard shortcut prevention for inspection
      const preventInspectShortcuts = (e: KeyboardEvent) => {
        // Common inspect element shortcuts
        if (
          (e.ctrlKey && e.shiftKey && e.key === 'C') ||
          (e.ctrlKey && e.shiftKey && e.key === 'I') ||
          (e.ctrlKey && e.shiftKey && e.key === 'J') ||
          (e.ctrlKey && e.key === 'U') ||
          (e.key === 'F12') ||
          (e.metaKey && e.altKey && e.key === 'I') ||
          (e.metaKey && e.altKey && e.key === 'J') ||
          (e.metaKey && e.altKey && e.key === 'C') ||
          (e.metaKey && e.key === 'U')
        ) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      };

      document.addEventListener('keydown', preventInspectShortcuts, true);

      const devToolsDetector = setInterval(() => {
        if (detector.check()) {
          clearInterval(devToolsDetector);
          detector.reset();
          // Redirect only in production
          if (import.meta.env.PROD) {
            window.location.href = '/';
          }
        }
      }, 1000);

      cleanupFunctions.push(() => {
        clearInterval(devToolsDetector);
        detector.reset();
        document.removeEventListener('mousedown', detector.preventInspection, true);
        document.removeEventListener('mouseover', detector.preventInspection, true);
        document.removeEventListener('contextmenu', detector.preventInspection, true);
        document.removeEventListener('keydown', preventInspectShortcuts, true);
      });
    }

    // Console disabling
    if (securityConfig.features.disableConsole && import.meta.env.PROD) {
      const originalConsole = { ...console };
      Object.keys(console).forEach((key) => {
        if (typeof (console as any)[key] === 'function') {
          (console as any)[key] = () => { };
        }
      });
      cleanupFunctions.push(() => {
        Object.assign(console, originalConsole);
      });
    }

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [currentPath, configLoaded]);
};

// Function to encrypt sensitive data before sending to API
const encryptData = (data: any): string => {
  if (!securityConfig.enabled || !securityConfig.features.encryptData) {
    return JSON.stringify(data);
  }

  const key = import.meta.env.VITE_ENCRYPTION_KEY || 'default-key';
  return btoa(JSON.stringify(data).split('').map((char, i) =>
    String.fromCharCode(char.charCodeAt(0) ^ key.charCodeAt(i % key.length))
  ).join(''));
};

// Function to decrypt received data
const decryptData = (encryptedData: string): any => {
  if (!securityConfig.enabled || !securityConfig.features.encryptData) {
    return JSON.parse(encryptedData);
  }

  const key = import.meta.env.VITE_ENCRYPTION_KEY || 'default-key';
  const decrypted = atob(encryptedData).split('').map((char, i) =>
    String.fromCharCode(char.charCodeAt(0) ^ key.charCodeAt(i % key.length))
  ).join('');
  return JSON.parse(decrypted);
}; 
